import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    getExcludeLayers,
    getIdentifyLayers,
    getIdentifyTolerance,
    getVisibleSubLayers,
    IdentifyResultsArray,
    identifyVectorLayers,
    sortIdentifyResults
} from "./identifyUtils";
import { Layer } from "ol/layer";
import { Overlay } from "ol";
import { useAppContext } from "../../Contexts/AppContext";
import { useMapContext } from "../../Contexts/MapContext";
import { getMap } from "../GeoLiteMap";
import { useMapDownloadContext } from "../../Contexts/MapDownloadContext";
import { identify } from "../rest";
import { useIdentifyContext } from "../../Contexts/IdentifyContext";
import { identifyWmsBasemap } from "./identifyWmsBasemap";
import { SheetSelectMode } from "../enums";
import { Polygon } from "ol/geom";
import "./Identify.scss";

export const Identify = (): JSX.Element => {
    const [identifiedFeatures, setIdentifiedFeatures] = useState<IdentifyResultsArray>();

    const { mapClick } = useAppContext();
    const { configA, layerStates, placingPin, identifyCoordinates, baseMap, setFeaturesPopup } = useMapContext();
    const { sheetSelectMode, setIdentifiedSheet, sheetSelectPolygon } = useMapDownloadContext();
    const { identifyResults, setResults, loading, setLoading, setCoordinates } = useIdentifyContext();

    const spinnerRef = useRef<Overlay>();

    useEffect(() => {
        spinnerRef.current = new Overlay({
            element: document.getElementById("identify-spinner"),
            offset: [0, -6],
            positioning: "bottom-center"
        });
        getMap().addOverlay(spinnerRef.current);
    }, []);

    useEffect(() => {
        if (!mapClick || placingPin) {
            return;
        }
        const coordinate = getMap().getEventCoordinate(mapClick.originalEvent);
        startIdentify(coordinate, mapClick.pixel);
    }, [mapClick]);

    useEffect(() => {
        if (identifiedFeatures) {
            const newResults: IdentifyResultsArray = identifyResults?.length
                ? [...identifyResults, ...identifiedFeatures]
                : [...identifiedFeatures];

            setResults(configA.identifyOrder ? sortIdentifyResults(newResults, configA.identifyOrder) : newResults);
        }
    }, [identifiedFeatures]);

    useEffect(() => {
        Array.isArray(identifiedFeatures) &&
            !identifiedFeatures.length &&
            !loading &&
            document.dispatchEvent(new Event("noIdentifyResults"));
    }, [identifiedFeatures, loading]);

    useEffect(() => {
        identifyCoordinates &&
            identifyCoordinates.coordinates &&
            startIdentify(
                identifyCoordinates.coordinates,
                getMap().getPixelFromCoordinate(identifyCoordinates.coordinates)
            );
    }, [identifyCoordinates]);

    const startIdentify = (coordinate: number[], pixel: number[]) => {
        setIdentifiedFeatures(null);
        setResults(null);
        setLoading(true);
        setFeaturesPopup(null);
        setCoordinates(coordinate);
        spinnerRef.current.setPosition(coordinate);
        identifyOnMap(coordinate, pixel);
    };

    const identifyOnMap = useCallback(
        async (coordinates: number[], pixel: number[]) => {
            if (sheetSelectMode) {
                const mapLayer = getMap()
                    .getLayers()
                    .getArray()
                    .find((l) => l.get("id") === configA.geotelpa.searchLayerId);
                if (!mapLayer) {
                    return;
                }

                if (sheetSelectMode === SheetSelectMode.Click) {
                    const res = await identifyLayer(mapLayer as Layer, coordinates, true);
                    res && res.length && setIdentifiedSheet([res[0][1][0].feature]);
                }

                if (sheetSelectMode === SheetSelectMode.Polygon && sheetSelectPolygon) {
                    const res = await identifyLayerByPolygon(
                        mapLayer as Layer,
                        sheetSelectPolygon.getGeometry() as Polygon
                    );
                    const features = res && res.length && res[0][1] && res[0][1].map((r) => r.feature);
                    features.length && setIdentifiedSheet(features);
                }

                setLoading(false);
                return;
            }
            const identifiedFeatures = identifyVectorLayers(getIdentifyLayers(configA, layerStates), pixel);
            identifiedFeatures && setIdentifiedFeatures(identifiedFeatures);

            await Promise.all(
                getIdentifyLayers(configA, layerStates).map(
                    async (mapLayer: Layer) => await identifyLayer(mapLayer, coordinates)
                )
            );

            const identifiedWmsFeatures = await identifyWmsBasemap(configA, pixel, baseMap);
            identifiedWmsFeatures && setIdentifiedFeatures(identifiedWmsFeatures);

            setLoading(false);
        },
        [sheetSelectMode, sheetSelectPolygon, layerStates]
    );

    const identifyLayer = async (mapLayer: Layer, coords: number[], identifySheet?: boolean) => {
        const params = getParams(`{"x":${coords[0]}, "y":${coords[1]}}`);
        if (!params) {
            return;
        }

        if (!identifySheet) {
            params.tolerance = getIdentifyTolerance(configA.identifyTolerance);
            params.layers = getVisibleSubLayers(configA.layers, mapLayer, getExcludeLayers(mapLayer, configA.layers));
        }
        if (params.layers === "visible:") {
            return;
        }

        const layerConfig = configA.layers.find((config) => config.id === mapLayer.get("id"));

        const result = await identify(
            configA.endpoints[layerConfig.endpoint.endpoint] + layerConfig.endpoint.url,
            params,
            layerConfig.id
        );
        !identifySheet && setIdentifiedFeatures(result);
        return result;
    };

    const identifyLayerByPolygon = async (mapLayer: Layer, geometry: Polygon) => {
        if (!geometry) {
            return;
        }

        const rings = JSON.stringify(geometry.getCoordinates());
        const params = getParams(`{"rings":${rings}}`);
        if (!params) {
            return;
        }

        params.geometryType = "esriGeometryPolygon";

        const layerConfig = configA.layers.find((config) => config.id === mapLayer.get("id"));

        const result = await identify(
            configA.endpoints[layerConfig.endpoint.endpoint] + layerConfig.endpoint.url,
            params,
            layerConfig.id
        );

        return result;
    };

    return (
        <div id="identify-spinner">
            {loading ? (
                <div className="spinner-container">
                    <div className="spinner"></div>
                </div>
            ) : null}
        </div>
    );
};

const getParams = (geometry: string): any => {
    const map = getMap();
    if (!map) {
        return;
    }
    const mapSize = map.getSize();
    const extent = map.getView().calculateExtent(mapSize);
    if (!mapSize || !extent) {
        return;
    }

    const params = {
        f: "json",
        imageDisplay: mapSize.toString() + ",96",
        geometry,
        geometryType: "esriGeometryPoint",
        sr: "3059",
        tolerance: 2,
        mapExtent: extent.toString(),
        returnGeometry: true,
        returnFieldName: false,
        returnUnformattedValues: false,
        layers: "all"
    };

    return params;
};
